import { getConversationMessageParts, getConversationMessageServerId } from 'utils';
import { parseAgentUrls } from 'utils/agent-urls';

const getConversationMessageAttachments = (message: any) => {
	const attachments = message.attachments || [];
	return attachments.map((attachment, index) => {
		if (!attachment.mediaId) throw new Error('Agent message attachments require mediaId.');
		return { mediaId: attachment.mediaId, position: index };
	});
};

export const createProjectOperatorRun = async ({ data = {}, instance, message, mode, rootApi, store }) => {
	const response = await store.createRecord({
		url: `${rootApi}/messages/${message.id}/runs`,
		params: { context: 'usage', include: ['outputMessage(attachments)', 'model'] },
		data: {
			status: 'queued',
			...(instance?.id ? { modelId: instance.id } : {}),
			mode,
			...data,
		},
	});
	return { message: response.data?.inputMessage || message, run: response.data };
};

const getConversationMessageRequestParts = (userMessage, promptResponse) => {
	const parts = getConversationMessageParts(userMessage);
	const responsePart = promptResponse?.responsePart;
	return responsePart ? [...parts, responsePart] : parts;
};

const getPromptData = prompt => prompt?.data || prompt || {};

const isProjectConceptPromptResponse = promptResponse => {
	const data = getPromptData(promptResponse?.prompt);
	const responseData = promptResponse?.responsePart?.data || {};
	return data.purpose === 'project-concepts' || responseData.purpose === 'project-concepts';
};

const getProjectConceptSelection = promptResponse => {
	if (!isProjectConceptPromptResponse(promptResponse)) return null;
	const data = getPromptData(promptResponse?.prompt);
	const responseData = promptResponse?.responsePart?.data || {};
	const response =
		Array.isArray(responseData.responses) && responseData.responses.length ? responseData.responses[0] : responseData;
	const selectedOption = Array.isArray(response?.selectedOptions) ? response.selectedOptions[0] : null;
	return {
		inputMessageId: data.inputMessageId,
		promptId: response?.promptId || responseData.promptId || data.id || promptResponse?.prompt?.id,
		conceptId: selectedOption?.id,
	};
};

export const createConversationMessage = async ({
	agentSuggestionId = null,
	deliveryMode = null,
	instance = null,
	mode = null,
	promptResponse = null,
	rootApi,
	store,
	text,
	userMessage,
	intentOverride = null,
	preflightOverride = null,
	preflightId = null,
	preflightSurface = null,
	replyToMessageId = null,
	targets = [],
	existingAttachments = [],
	environment = 'sandbox',
}) => {
	const parts = getConversationMessageRequestParts(userMessage, promptResponse);
	const attachments = [...getConversationMessageAttachments(userMessage), ...existingAttachments]
		.filter(attachment => attachment?.mediaId)
		.filter((attachment, index, values) => values.findIndex(value => value.mediaId === attachment.mediaId) === index)
		.map((attachment, position) => ({ mediaId: attachment.mediaId, position }));
	const response = await store.createRecord({
		url: `${rootApi}/messages`,
		params: { context: 'usage', include: ['attachments', 'targetRun.[model]', 'latestRun.[model]'] },
		data: {
			environment,
			role: 'user',
			type: promptResponse ? 'answer' : 'message',
			status: 'completed',
			text,
			urls: parseAgentUrls(text).map(({ url, position }) => ({ url, position })),
			parts,
			attachments,
			...(intentOverride ? { intentOverride } : {}),
			...(preflightOverride ? { preflightOverride } : {}),
			...(preflightId ? { preflightId } : {}),
			...(preflightSurface ? { preflightSurface } : {}),
			...(replyToMessageId ? { replyToMessageId } : {}),
			...(targets.length ? { targets } : {}),
			...(deliveryMode ? { deliveryMode } : {}),
			...(!promptResponse && mode ? { mode } : {}),
			...(!promptResponse && instance?.id ? { modelId: instance.id } : {}),
			...(agentSuggestionId ? { agentSuggestionId } : {}),
		},
	});
	return { message: response.data };
};

export const respondToAgentPrompt = async ({
	instance = null,
	mode = null,
	promptResponse,
	rootApi,
	store,
	text,
	userMessage,
	environment = 'sandbox',
}) => {
	const promptMessageId = promptResponse?.promptMessageId;
	if (!promptMessageId) throw new Error('Structured responses require an originating prompt message.');
	const response = await store.createRecord({
		url: `${rootApi}/messages/${promptMessageId}/responses`,
		data: {
			environment,
			idempotencyKey: crypto.randomUUID(),
			mode,
			...(instance?.id ? { modelId: instance.id } : {}),
			text,
			parts: getConversationMessageRequestParts(userMessage, promptResponse),
		},
	});
	return response.data;
};

export const deleteQueuedConversationMessage = async ({ message, rootApi, store }) => {
	const response = await store.deleteRecord({ url: `${rootApi}/messages/${message.id}` });
	return { message: response.data };
};

export const reorderQueuedConversationMessages = async ({ messages, rootApi, store }) => {
	const response = await store.updateRecord({
		url: `${rootApi}/messages/queue`,
		data: { messageIds: messages.map(message => message.id) },
	});
	return { messages: response.data };
};

export const steerQueuedConversationMessage = async ({ message, rootApi, store }) => {
	const response = await store.createRecord({ url: `${rootApi}/messages/${message.id}/steer`, data: {} });
	return { message: response.data };
};

export const createProjectOperatorMessageRun = async ({
	agentSuggestionId = null,
	instance,
	mode,
	promptResponse = null,
	rootApi,
	store,
	text,
	userMessage,
	targets = [],
	preflightId = null,
	preflightOverride = null,
	preflightSurface = null,
	environment = 'sandbox',
}) => {
	const parts = getConversationMessageRequestParts(userMessage, promptResponse);
	if (!promptResponse && getConversationMessageServerId(userMessage) && text === userMessage.text) {
		return createProjectOperatorRun({
			instance,
			message: {
				id: getConversationMessageServerId(userMessage),
				branchId: userMessage.branchId,
				createdAt: userMessage.createdAt,
				owner: userMessage.owner,
				parts,
				role: 'user',
				status: 'completed',
				text,
				updatedAt: userMessage.updatedAt,
			},
			mode,
			rootApi,
			store,
		});
	}

	const { message } = await createConversationMessage({
		instance,
		mode,
		promptResponse,
		rootApi,
		store,
		text,
		userMessage,
		targets,
		preflightId,
		preflightOverride,
		preflightSurface,
		environment,
		agentSuggestionId,
	});
	const projectConceptSelection = getProjectConceptSelection(promptResponse);
	if (projectConceptSelection?.inputMessageId) {
		return createProjectOperatorRun({
			instance,
			message: { id: projectConceptSelection.inputMessageId },
			mode: 'execute',
			rootApi,
			store,
			data: {
				projectConceptPromptId: projectConceptSelection.promptId,
				projectConceptId: projectConceptSelection.conceptId,
				projectConceptResponseMessageId: message.id,
			},
		});
	}
	return createProjectOperatorRun({ instance, message, mode, rootApi, store });
};

export const fetchProjectOperatorSuggestions = async ({ rootApi, store }) => {
	const response = await store.request({
		url: `${rootApi}/suggestions`,
		params: { pageSize: '*', sortProp: 'position', sortValue: 'asc' },
	});
	return { suggestions: response.data?.agentSuggestions || response.data?.suggestions || response.data || [] };
};

export const regenerateProjectOperatorSuggestions = async ({ rootApi, store }) => {
	const response = await store.createRecord({ url: `${rootApi}/suggestions/regenerate` });
	return { suggestions: response.data?.agentSuggestions || response.data?.suggestions || response.data || [] };
};

export const dismissProjectOperatorSuggestion = async ({ rootApi, store, suggestion }) => {
	const response = await store.updateRecord({
		url: `${rootApi}/suggestions/${suggestion.id}`,
		data: { status: 'dismissed' },
	});
	return { suggestion: response.data };
};
